import { inject, Pipe, PipeTransform } from '@angular/core';
import { ShareableReference } from '@bindoc-projects/resource-domain-lib';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { BdPermissionsCompanyService } from '../company/company.service';
import { BdPermissionsGroupsService } from './groups.service';

/**
 * Resolves the display name for a share target — enhancing group grants with the
 * group's own name **and** its owning company whenever the current user is
 * allowed to resolve the group.
 *
 * The permission endpoints already return a `name` for every target, but for
 * group grants that is only a descriptive share-group label that many users
 * cannot resolve from their own groups (BDA-5618). This pipe therefore
 * *attempts* the authoritative
 * group/company lookup (`groups/{id}` → `companies/{id}`) and only uses its
 * result when the request is allowed and returns a 2xx response. Any failure
 * (typically a 403 for a group the user does not belong to) falls back to the
 * previous behaviour: the API-provided `name`, or the id as a last resort.
 *
 * Non-group targets skip the lookup entirely and use the same fallback.
 *
 * Since BDA-5990 the access graph resolves a group's owning company itself, so
 * the "Zugriff" table no longer needs this pipe. It remains for the callers
 * whose share target comes from an endpoint that does not carry the company.
 *
 * Pure pipe: `transform` only re-runs when the reference changes, so a stable
 * `shareTarget` object triggers exactly one lookup. Consume with `| async`.
 */
@Pipe({
  name: 'groupDisplayName',
  standalone: true,
})
export class BdGroupDisplayNamePipe implements PipeTransform {
  private readonly _groupsService = inject(BdPermissionsGroupsService);
  private readonly _companyService = inject(BdPermissionsCompanyService);

  transform(reference: ShareableReference): Observable<string> {
    const fallback = reference.name?.trim() || reference.id;

    if (reference.type !== 'group') {
      return of(fallback);
    }

    return this._groupsService.byId(reference.id).pipe(
      switchMap((group) => {
        if (!group) {
          return of(fallback);
        }
        return this._companyService.getById(group.companyId).pipe(
          map((company) =>
            company?.name ? `${company.name} / ${group.name}` : group.name,
          ),
          // The group resolved but the company did not — still an improvement
          // over the descriptive label, so keep the group name.
          catchError(() => of(group.name)),
        );
      }),
      // Group lookup was not allowed (403) or failed — keep the current
      // API-provided name.
      catchError(() => of(fallback)),
    );
  }
}
